<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: Stamboom                                              |
| Section: views/trees                                             |
| Purpose: Unified stamboom shell combining the personen-netwerk,  |
|          achternamen tree, and familieleden views in one route.  |
+------------------------------------------------------------------+
-->
<template>
    <div class="stamboom-wrapper">
        <ImmersiveBackLink />
        <component :is="activeTreeView" v-if="activeTreeView" :key="`${currentView}:${cacheEpoch}`"
            :initial-state="initialTreeState" @state-change="cacheTreeState" />
        <div v-if="currentView === 'relaties'" class="stamboom-relaties-scroll" key="relaties">
            <SiteStatsRelations :cooperativeGestures="false" />
        </div>

        <nav class="stamboom-view-toggles" aria-label="Stamboom weergave">
            <button :class="{ active: currentView === 'personen' }" @click="setView('personen')">{{
                $t('stamboom.togglePersonen') }}</button>
            <button :class="{ active: currentView === 'achternamen' }" @click="setView('achternamen')">{{
                $t('stamboom.toggleAchternamen') }}</button>
            <button :class="{ active: currentView === 'relaties' }" @click="setView('relaties')">{{
                $t('stamboom.toggleRelaties') }}</button>
        </nav>
    </div>
</template>

<script>
import PersonTree from '@/components/PersonTree.vue';
import SurnameTree from '@/components/SurnameTree.vue';
import SiteStatsRelations from '@/views/overviews/SiteStatsRelations.vue';
import ImmersiveBackLink from '@/components/ImmersiveBackLink.vue';
import { applyGenericSeo } from '@/utils/seoUtils';
import { markRaw } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { hasLivingPersonAccess } from '@/utils/personPrivacy';
import { isCompatibleTreeViewState } from '@/utils/treePerformance.mjs';

export default {
    name: 'StamboomView',
    components: {
        PersonTree,
        SurnameTree,
        SiteStatsRelations,
        ImmersiveBackLink,
    },
    setup() {
        const { authToken } = useAuth();
        return { authToken };
    },
    data() {
        return {
            cachedTreeStates: Object.create(null),
            cacheEpoch: 0,
            suppressStateCache: false,
        };
    },
    computed: {
        currentView() {
            const v = this.$route.query.view;
            return v === 'achternamen' || v === 'relaties' ? v : 'personen';
        },
        activeTreeView() {
            if (this.currentView === 'personen') return 'PersonTree';
            if (this.currentView === 'achternamen') return 'SurnameTree';
            return null;
        },
        privacyScope() {
            return hasLivingPersonAccess(this.authToken) ? 'member' : 'public';
        },
        initialTreeState() {
            const state = this.cachedTreeStates[this.currentView];
            return isCompatibleTreeViewState(state, this.privacyScope, this.currentView) ? state : null;
        },
    },
    watch: {
        privacyScope() {
            this.suppressStateCache = true;
            this.clearTreeStateCache();
            this.cacheEpoch += 1;
            this.$nextTick(() => {
                this.clearTreeStateCache();
                this.suppressStateCache = false;
            });
        },
    },
    methods: {
        setView(view) {
            const query = view !== 'personen' ? { view } : {};
            this.$router.replace({ query });
        },
        cacheTreeState(state) {
            if (this.suppressStateCache) return;
            const view = state?.view?.type;
            if (!view || !isCompatibleTreeViewState(state, this.privacyScope, view)) return;
            this.cachedTreeStates[view] = markRaw(state);
        },
        clearTreeStateCache() {
            this.cachedTreeStates = Object.create(null);
        },
    },
    mounted() {
        this.clearTreeStateCache();
        const locale = this.$route?.params?.locale;
        const localizedPath = (path) => (locale ? `/${locale}${path}` : path);
        applyGenericSeo({
            title: 'Stamboom | Sejarah Thümann',
            description: 'Verken de volledige stamboom: het personen-netwerk, achternamen en familieleden.',
            image: 'https://sejarah-thumann.online/img/sejarah_thumann1080x781.4fc54a6a.jpg',
            url: localizedPath('/stamboom'),
        });
    },
    beforeUnmount() {
        this.clearTreeStateCache();
    },
};
</script>

<style scoped>
.stamboom-wrapper {
    position: relative;
    width: 100%;
    height: 100vh;
    overflow: hidden;
}

.stamboom-relaties-scroll {
    width: 100%;
    height: 100vh;
    overflow: hidden;
    padding-bottom: 0;
}

.stamboom-view-toggles {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1300;
    display: flex;
    gap: 4px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 999px;
    padding: 5px 6px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
    backdrop-filter: blur(6px);
    border: 1px solid rgba(0, 0, 0, 0.07);
}

.stamboom-view-toggles button {
    padding: 7px 20px;
    border: none;
    background: transparent;
    border-radius: 999px;
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #555;
    transition: background 0.18s, color 0.18s;
    white-space: nowrap;
}

.stamboom-view-toggles button:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #111;
}

.stamboom-view-toggles button.active {
    background: #ae1e25;
    color: #fff;
}
</style>
